import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { getIdTokenResult, onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth'
import { adminAuth, adminDb, isAdminFirebaseConfigured } from '../../../../packages/firebase/adminClient'
import { hasAdminClaim, isValidAdminRole } from './adminAuthorization'
import { exchangeKakaoCode, startKakaoLogin } from './kakaoWebAuth'
import { exchangeViewerPassword } from './viewerWebAuth'
import { invalidateAdminApiClientRefresh } from '../lib/adminApiClient'

const AuthContext = createContext(null)

/**
 * admins/{uid}가 권한의 원본이다(functions/api/admin/_lib/auth.js의 requireAdmin과 동일 모델) —
 * custom claim은 동기화된 projection일 뿐이다. 그래서 클라이언트도 claim만 보고 판단하지 않고
 * 본인 admins/{uid} 문서를 직접 읽는다: firestore.rules의 isNewAdmin()이 정확히 이 read를
 * 게이팅하므로, 읽기가 성공하면 그 자체가 인가 판단이고 role도 문서에서 바로 얻는다(claim이
 * 아직 동기화되지 않은 임시 allowlist 상태에서도 서버와 동일하게 통과한다).
 */
async function resolveAuthorization(user) {
  const tokenResult = await getIdTokenResult(user, true)
  try {
    const snapshot = await getDoc(doc(adminDb, 'admins', user.uid))
    const account = snapshot.exists() ? snapshot.data() : null
    if (account?.enabled === true && isValidAdminRole(account.role)) {
      return { authorized: true, source: hasAdminClaim(tokenResult) ? 'custom-claim' : 'temporary-allowlist', role: account.role, tokenResult }
    }
  } catch {
    // Firestore rules가 read 자체를 거부함 — enabled된 admins 문서가 없거나 claim이 필요한데 없는 상태.
  }
  return { authorized: false, source: 'none', role: null, tokenResult }
}

export function AdminAuthProvider({ children }) {
  const [state, setState] = useState({ status: isAdminFirebaseConfigured ? 'loading' : 'configuration-error', user: null, source: null, role: null, error: null })

  /**
   * userRef: Firebase currentUser를 항상 최신값으로 유지하는 stable ref.
   * getIdToken 함수가 state 전체에 의존하지 않도록 분리한다.
   * 이 덕분에 auth phase가 loading→checking→authorized로 전환되는 동안
   * getIdToken 함수 참조가 바뀌지 않아 analytics 등 downstream effect가
   * 불필요하게 재실행되지 않는다.
   */
  const userRef = useRef(null)

  useEffect(() => {
    if (import.meta.env.DEV && ((typeof localStorage !== 'undefined' && localStorage.getItem('admin_dev_mode') === 'true') || window.__SUGUIDE_ADMIN_DEV_AUTH__)) {
      const devUser = { uid: 'dev-admin', email: 'admin@su-guide.local', getIdToken: async () => 'dev-token' }
      userRef.current = devUser
      setState({ status: 'authorized', user: devUser, source: 'custom-claim', role: 'owner', error: null })
      return undefined
    }
    if (!adminAuth) return undefined
    return onAuthStateChanged(adminAuth, async user => {
      if (userRef.current !== user) invalidateAdminApiClientRefresh()
      if (!user) {
        userRef.current = null
        setState({ status: 'signed-out', user: null, source: null, role: null, error: null })
        return
      }
      userRef.current = user
      setState({ status: 'checking', user, source: null, role: null, error: null })
      try {
        const access = await resolveAuthorization(user)
        setState({ status: access.authorized ? 'authorized' : 'forbidden', user, source: access.source, role: access.role, error: null })
      } catch (error) {
        setState({ status: 'error', user, source: null, role: null, error })
      }
    })
  }, [])

  const login = useCallback(() => {
    if (import.meta.env.DEV) {
      if (typeof localStorage !== 'undefined') localStorage.setItem('admin_dev_mode', 'true')
      const devUser = { uid: 'dev-admin', email: 'admin@su-guide.local', getIdToken: async () => 'dev-token' }
      userRef.current = devUser
      setState({ status: 'authorized', user: devUser, source: 'custom-claim', role: 'owner', error: null })
      return
    }
    if (!adminAuth) throw new Error('Firebase 설정이 없습니다.')
    startKakaoLogin()
  }, [])

  const completeKakaoLogin = useCallback(async (code, state) => {
    if (!adminAuth) throw new Error('Firebase 설정이 없습니다.')
    const firebaseToken = await exchangeKakaoCode(code, state)
    await signInWithCustomToken(adminAuth, firebaseToken)
  }, [])

  const loginViewer = useCallback(async password => {
    if (!adminAuth) throw new Error('Firebase 설정이 없습니다.')
    const firebaseToken = await exchangeViewerPassword(password)
    await signInWithCustomToken(adminAuth, firebaseToken)
  }, [])

  // owner가 이 계정의 역할을 바꾼 뒤 재로그인 없이 즉시 반영하고 싶을 때 쓴다 — ID 토큰을 강제
  // 갱신하고 admins/{uid} 문서를 다시 읽어 role/authorized 상태를 갱신한다.
  const refreshAuthorization = useCallback(async () => {
    if (!userRef.current) return
    setState(current => ({ ...current, status: 'checking' }))
    try {
      const access = await resolveAuthorization(userRef.current)
      setState(current => ({ ...current, status: access.authorized ? 'authorized' : 'forbidden', source: access.source, role: access.role, error: null }))
    } catch (error) {
      setState(current => ({ ...current, status: 'error', source: null, role: null, error }))
    }
  }, [])

  /**
   * getIdToken: 하위 호환성을 위해 유지.
   * analytics/growth hook은 adminApiClient를 통해 자동 처리되므로 이 함수를 deps로 사용하지 않는다.
   * deps가 [] 이므로 state 변경에 의해 새 참조가 생성되지 않는다.
   */
  const getIdToken = useCallback(async (forceRefresh = false) => userRef.current?.getIdToken(forceRefresh) ?? null, [])

  const logout = useCallback(async () => {
    if (adminAuth) await signOut(adminAuth)
  }, [])

  const value = useMemo(() => ({
    ...state,
    login,
    loginViewer,
    completeKakaoLogin,
    refreshAuthorization,
    logout,
    getIdToken,
  }), [state, login, loginViewer, completeKakaoLogin, refreshAuthorization, logout, getIdToken])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAdminAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAdminAuth must be used inside AdminAuthProvider')
  return context
}
